// ---------------------------------------------------------------------------
// Bioregional Capital Partners page (/capital-partners).
// From the master strategy brief. Bioregional Capital Partners is the
// regenerative investment initiative of Bioregional Ventures. Initial capital
// has been committed (mixed principal and private investor sources); legal and
// structural arrangements are being completed before deployment. No fund,
// securities offer, lending programme or guaranteed finance may be implied
// anywhere in this content.
// ---------------------------------------------------------------------------

export const capitalPartners = {
  parentBrand: "Bioregional Ventures",
  name: "Bioregional Capital Partners",
  standfirst: "Investing in the wealth of place.",
  heroBody:
    "Bioregional Capital Partners is an emerging investment platform supported by initial capital commitments from principal and private investors. Our focus is on identifying and developing regenerative investment opportunities that combine financial discipline with long-term ecological and community value.",
  heroBodySecond:
    "Bioregional Capital Partners connects family wealth and patient investment with practical opportunities to build a sustainable future. We are developing opportunities that bring productive land, local enterprises and shared infrastructure together, with the aim of creating lasting value for communities and the ecosystems that sustain them. Our aim is to align long-term financial returns with resilient local economies, giving capital a meaningful role in the places and livelihoods future generations will depend on.",
  heroCta: "Explore a Partnership",

  themesHeading: "Investment themes",
  themes: [
    {
      title: "Productive land and regenerative agriculture",
      text: "Farms, orchards and land-based businesses with credible operating income, asset quality and opportunities for improved stewardship.",
    },
    {
      title: "Regenerative enterprises",
      text: "Seed production, nurseries, food systems, agricultural services and related businesses with identifiable customers and potential to scale responsibly.",
    },
    {
      title: "Productive community infrastructure",
      text: "Facilities and shared assets that can generate rental, trading or service revenue while supporting regional livelihoods.",
    },
  ],
  themesNote: "",

  principlesIntro:
    "A compelling environmental mission does not replace due diligence, realistic revenue, strong governance or a clear understanding of risk.",

  communityWealthHeading: "Investing in Community Wealth",
  communityWealthBody: [
    "Central to our philosophy is the belief that productive assets and essential infrastructure should, wherever possible, remain under community ownership, control and management.",
    "We explore financing arrangements that help communities acquire, develop and retain these assets while providing investors with appropriate financial returns.",
    "Through cooperative ownership, community land trusts, patient capital and other suitable structures, investment can strengthen local autonomy rather than create permanent dependence on outside capital.",
  ],

  principlesHeading: "Our Investment Principles",
  principles: [
    { title: "Responsible returns", text: "Preserve and grow capital through financially sound investments, recognising and managing investment risks." },
    { title: "Patient capital", text: "Align investment horizons with the time required to establish productive enterprises and restore ecological systems." },
    { title: "Community control", text: "Strengthen local ownership, governance and management of productive assets and infrastructure." },
    { title: "Regenerative value", text: "Support investments that improve ecological health, local livelihoods and regional resilience." },
    { title: "Continuing reinvestment", text: "Recycle returns and available capital into further opportunities, creating an enduring capacity for regeneration." },
  ],
  principlesNote:
    "These principles guide our approach. They do not eliminate investment risk or guarantee capital preservation.",

  structureHeading: "How investments might be structured",
  structureBody: [
    "Depending on the opportunity and legal advice, potential approaches may include direct asset ownership, equity participation, secured project lending, revenue-sharing or project-specific co-investment. Each has different risks, tax consequences, governance needs and regulatory requirements.",
    "Some activities generate trading income; others create the conditions that allow those enterprises to succeed. We explore coordinated funding for connected activities, including shared infrastructure, training, ecological restoration and market development, with each component supported by an appropriate source of finance.",
    "We are not currently advertising a fund, offering securities, taking investment subscriptions or promising financing to project proponents.",
  ],

  partnersHeading: "Capital partners and philanthropy",
  partnersBody: [
    "We welcome exploratory conversations with private investors, family offices and others interested in patient, disciplined regenerative investment. Philanthropic contributions may separately support non-commercial public-benefit components of eligible projects, subject to appropriate charitable arrangements and safeguards.",
    "Investment capital and philanthropic gifts have different purposes, rights and obligations. Any future co-investment or donor structure would require appropriate professional review before launch.",
  ],

  relationshipHeading: "How it relates to Bioregional Ventures",
  relationshipBody: [
    "Bioregional Ventures identifies and develops projects through feasibility, business planning, governance, partnerships and implementation support.",
    "Bioregional Capital Partners evaluates investment opportunities to deploy or coordinate committed capital. The projects may be developed internally or in partnerships with other organisations. There is no guarantee that a project will receive investment.",
  ],

  stageHeading: "Current stage and invitation",
  stageBody:
    "As the platform takes shape, we will invite exploratory conversations about credible funding opportunities and potential future partnerships.",
  stageCta: "Discuss an Investment or Project Partnership",

  disclosure:
    "This information is general and is not an offer to invest, an invitation to acquire financial products or a representation that financing is available.",
};

// Suggested footer note for all finance-related pages (master brief section 10).
export const financeDisclosure =
  "Bioregional Ventures provides consulting, project development and funding support and is establishing a regenerative investment initiative with committed capital. Information on financing approaches is general and does not constitute financial, legal, tax or investment advice. No investment opportunity or financing facility is offered through this website.";
